/**
 * Onglet « Analyses » — rentabilité par axe et période (A5 Finance).
 * Vocabulaire : marge brute, marge contributive. Le bénéfice net comptable n'est pas calculé (mention affichée).
 * Données internes : réservé à `marge.lire` (back-office) ; jamais exposé au portail.
 */
import { useMemo, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Alert, Badge, Button, Card, SelectField, Table, TextField } from '@myron/design-system';
import { Download } from 'lucide-react';
import { ApiErreur, get } from '../../../shared/api/client';
import { EtatRequete } from '../../../shared/ui/composants';
import { euros } from '../../../shared/ui/format';
import { BarreFiltres } from '../commercial/commun';

/** Types locaux en attendant la fusion du fragment `docs/contrats/fragments/analyses.yaml` dans le contrat. */
export interface LigneAnalyse {
  cle: string; libelle: string; detail?: string; nbCommandes: number;
  ventesBrutesHtCentimes: number; remisesHtCentimes: number; retoursAvoirsHtCentimes: number; avoirsCommerciauxHtCentimes: number;
  ventesNettesHtCentimes: number; coutMarchandisesVenduesCentimes: number; coutReprisRetoursCentimes: number; coutNetMarchandisesCentimes: number;
  pertesCasseCentimes: number; fraisApprocheInclusCentimes: number;
  margeBruteCentimes: number; tauxMargeBruteBp: number | null;
  fraisPortFacturesHtCentimes: number; coutsVariablesCentimes: number; transportSortantCentimes: number;
  margeContributiveCentimes: number; tauxMargeContributiveBp: number | null;
  lignesACompleter: number; statut: 'OK' | 'A_COMPLETER';
}
export interface AnalyseRentabilite {
  axe: Axe; periode: { du: string | null; au: string | null }; base: string;
  lignes: LigneAnalyse[]; totaux: LigneAnalyse;
  synthese: {
    ventesNettesHtCentimes: number; margeBruteCentimes: number; tauxMargeBruteBp: number | null;
    margeContributiveCentimes: number; tauxMargeContributiveBp: number | null;
    coutsVariables: { code: string; libelle: string; nature: string; montantCentimes: number }[];
    fraisApprocheInclusCentimes: number;
    transportSortant: { confirmeNbCommandes: number; estimeNbCommandes: number; montantCentimes: number };
    pertesCasseRetoursCentimes: number;
    ajustementsStock: { pertesCentimes: number; gainsCentimes: number; nbMouvements: number; nbNonValorises: number };
    margeContributiveApresAjustementsCentimes: number;
    lignesACompleter: number;
    beneficeNetComptableCentimes: null;
    mentionBeneficeNet: string;
  };
}

type Axe = 'produit' | 'conditionnement' | 'commande' | 'client' | 'segment' | 'grossiste' | 'fournisseur' | 'periode';
const AXES: { value: Axe; label: string; titre: string }[] = [
  { value: 'produit', label: 'Produit', titre: 'Produit' },
  { value: 'conditionnement', label: 'Conditionnement (unité, carton, palette)', titre: 'Conditionnement' },
  { value: 'commande', label: 'Commande', titre: 'Commande' },
  { value: 'client', label: 'Client', titre: 'Client' },
  { value: 'segment', label: 'Segment (détaillants / grossistes)', titre: 'Segment' },
  { value: 'grossiste', label: 'Grossiste', titre: 'Grossiste' },
  { value: 'fournisseur', label: 'Fournisseur', titre: 'Fournisseur' },
  { value: 'periode', label: 'Période (mois)', titre: 'Mois' },
];
type Tri = 'ventes' | 'margeBrute' | 'tauxBrut' | 'margeContributive' | 'tauxContributif' | 'libelle';
const TRIS: { value: Tri; label: string }[] = [
  { value: 'ventes', label: 'Ventes nettes (décroissant)' },
  { value: 'margeBrute', label: 'Marge brute (décroissant)' },
  { value: 'tauxBrut', label: 'Taux de marge brute (croissant)' },
  { value: 'margeContributive', label: 'Marge contributive (décroissant)' },
  { value: 'tauxContributif', label: 'Taux de marge contributive (croissant)' },
  { value: 'libelle', label: 'Libellé (A → Z)' },
];

const taux = (bp: number | null | undefined) => (bp === null || bp === undefined ? '—' : `${(bp / 100).toLocaleString('fr-FR', { minimumFractionDigits: 1, maximumFractionDigits: 1 })} %`);
const signe = (c: number) => <span className="my-num" style={c < 0 ? { color: 'var(--erreur)', fontWeight: 600 } : undefined}>{euros(c)}</span>;
const debutMois = () => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-01`; };

export function MentionBeneficeNet({ texte }: { texte?: string }) {
  return (
    <Alert ton="info" titre="Bénéfice net comptable : non calculé">
      {texte ?? 'Le bénéfice net comptable nécessite les charges fixes (loyers, salaires de structure, amortissements), les charges financières et l’impôt, qui ne sont pas gérés dans MYRON.'} Les montants présentés ici sont des marges (brute et contributive), pas un bénéfice.
    </Alert>
  );
}

export function OngletAnalyses() {
  const [axe, setAxe] = useState<Axe>('produit');
  const [du, setDu] = useState('');
  const [au, setAu] = useState('');
  const [tri, setTri] = useState<Tri>('ventes');
  const [lien, setLien] = useState<{ url: string; nomFichier: string; lignes: number } | null>(null);
  const r = useQuery({ queryKey: ['admin', 'analyses', 'rentabilite', axe, du, au], queryFn: () => get<AnalyseRentabilite>('/admin/analyses/rentabilite', { axe, du, au }) });
  const exporter = useMutation({
    mutationFn: () => get<{ url: string; nomFichier: string; lignes: number }>('/admin/analyses/rentabilite/export', { axe, du, au }),
    onSuccess: (d) => setLien(d),
  });
  const titreAxe = AXES.find((a) => a.value === axe)!.titre;
  const lignesTriees = useMemo(() => {
    const l = [...(r.data?.lignes ?? [])];
    const nz = (v: number | null) => (v === null ? Number.POSITIVE_INFINITY : v);
    switch (tri) {
      case 'ventes': return l.sort((a, b) => b.ventesNettesHtCentimes - a.ventesNettesHtCentimes);
      case 'margeBrute': return l.sort((a, b) => b.margeBruteCentimes - a.margeBruteCentimes);
      case 'tauxBrut': return l.sort((a, b) => nz(a.tauxMargeBruteBp) - nz(b.tauxMargeBruteBp));
      case 'margeContributive': return l.sort((a, b) => b.margeContributiveCentimes - a.margeContributiveCentimes);
      case 'tauxContributif': return l.sort((a, b) => nz(a.tauxMargeContributiveBp) - nz(b.tauxMargeContributiveBp));
      case 'libelle': return axe === 'periode' ? l.sort((a, b) => a.cle.localeCompare(b.cle)) : l.sort((a, b) => a.libelle.localeCompare(b.libelle, 'fr'));
    }
  }, [r.data, tri, axe]);

  return (
    <div className="my-pile">
      <BarreFiltres>
        <SelectField label="Analyser par" value={axe} onChange={(e) => { setAxe(e.target.value as Axe); setLien(null); }} options={AXES} />
        <TextField label="Du" type="date" value={du} onChange={(e) => { setDu(e.target.value); setLien(null); }} />
        <TextField label="Au" type="date" value={au} onChange={(e) => { setAu(e.target.value); setLien(null); }} />
        <SelectField label="Trier par" value={tri} onChange={(e) => setTri(e.target.value as Tri)} options={TRIS} />
        <div className="my-ligne">
          <Button compact variante="tertiaire" onClick={() => { setDu(debutMois()); setAu(''); setLien(null); }}>Mois en cours</Button>
          <Button compact variante="tertiaire" onClick={() => { setDu(`${new Date().getFullYear()}-01-01`); setAu(''); setLien(null); }}>Année</Button>
          <Button compact variante="tertiaire" onClick={() => { setDu(''); setAu(''); setLien(null); }}>Tout</Button>
        </div>
      </BarreFiltres>

      <EtatRequete requete={r} vide={(d) => !d.lignes.length} titreVide="Aucune vente réalisée sur la période" enfants={(d) => (
        <>
          {d.synthese.lignesACompleter > 0 && <Alert ton="alerte" titre="Marge à compléter">{d.synthese.lignesACompleter} ligne(s) vendue(s) sans coût figé : les marges des lignes concernées sont surestimées tant que le coût n’est pas complété.</Alert>}
          <Synthese a={d} />
          <Card as="section" aria-labelledby="t-analyse" pad={false}>
            <div className="my-ligne" style={{ justifyContent: 'space-between', padding: '16px 16px 8px', flexWrap: 'wrap', gap: 8 }}>
              <div>
                <h2 id="t-analyse" className="my-titre-4">Rentabilité par {titreAxe.toLowerCase()}</h2>
                <p className="my-petit my-secondaire">{d.base}{['produit', 'conditionnement', 'fournisseur'].includes(axe) ? ' Port, coûts variables et avoirs commerciaux répartis sur les lignes au prorata de leur vente nette.' : ''}</p>
              </div>
              <Button variante="secondaire" icone={Download} disabled={exporter.isPending} onClick={() => exporter.mutate()}>Exporter (CSV)</Button>
            </div>
            {exporter.error && <div style={{ padding: '0 16px' }}><Alert ton="erreur" titre="Export impossible">{exporter.error instanceof ApiErreur ? exporter.error.message : 'Erreur inattendue'}</Alert></div>}
            {lien && <div style={{ padding: '0 16px 8px' }}><Alert ton="succes" titre="Export prêt"><a href={lien.url} download={lien.nomFichier}>Télécharger {lien.nomFichier}</a> — {lien.lignes} ligne(s), formules neutralisées, export journalisé.</Alert></div>}
            <Table<LigneAnalyse & { total?: boolean }>
              legende={`Rentabilité par ${titreAxe.toLowerCase()}`}
              lignes={[...lignesTriees, { ...d.totaux, total: true }]}
              cle={(l) => l.cle}
              colonnes={[
                { cle: 'l', titre: titreAxe, rendu: (l) => (l.total ? <strong data-testid="analyse-total">Total</strong> : <span>{l.libelle}{l.detail && <span className="my-secondaire my-petit"> · {l.detail}</span>}</span>) },
                { cle: 'n', titre: 'Cmd', num: true, rendu: (l) => l.nbCommandes },
                { cle: 'v', titre: 'Ventes nettes HT', num: true, rendu: (l) => <span className="my-num" data-testid={l.total ? 'total-ventes-nettes' : undefined}>{euros(l.ventesNettesHtCentimes)}</span> },
                { cle: 'r', titre: 'Remises', num: true, rendu: (l) => euros(l.remisesHtCentimes) },
                { cle: 'c', titre: 'Coût marchandises', num: true, rendu: (l) => euros(l.coutNetMarchandisesCentimes) },
                { cle: 'mb', titre: 'Marge brute', num: true, rendu: (l) => <span data-testid={l.total ? 'total-marge-brute' : undefined}>{signe(l.margeBruteCentimes)}{l.statut === 'A_COMPLETER' && <> <Badge ton="alerte">À compléter</Badge></>}</span> },
                { cle: 'tb', titre: 'MB %', num: true, rendu: (l) => taux(l.tauxMargeBruteBp) },
                { cle: 'cv', titre: 'Coûts variables', num: true, rendu: (l) => euros(l.coutsVariablesCentimes) },
                { cle: 'mc', titre: 'Marge contributive', num: true, rendu: (l) => <span data-testid={l.total ? 'total-marge-contributive' : undefined}>{signe(l.margeContributiveCentimes)}</span> },
                { cle: 'tc', titre: 'MC %', num: true, rendu: (l) => taux(l.tauxMargeContributiveBp) },
              ]}
            />
          </Card>
          <MentionBeneficeNet texte={d.synthese.mentionBeneficeNet} />
        </>
      )} />
    </div>
  );
}

function Synthese({ a }: { a: AnalyseRentabilite }) {
  const s = a.synthese;
  return (
    <div className="my-grille-2">
      <Card as="section" aria-labelledby="t-synth" style={{ minWidth: 0 }}>
        <h2 id="t-synth" className="my-titre-4" style={{ marginBottom: 12 }}>Synthèse MYRON sur la période</h2>
        <dl className="my-dl">
          <dt>Ventes nettes HT</dt><dd className="my-num">{euros(s.ventesNettesHtCentimes)}</dd>
          <dt style={{ fontWeight: 600 }}>Marge brute</dt><dd className="my-prix" data-testid="synthese-marge-brute">{euros(s.margeBruteCentimes)} <span className="my-secondaire my-petit">({taux(s.tauxMargeBruteBp)})</span></dd>
          {s.coutsVariables.map((c) => <div key={c.code} style={{ display: 'contents' }}><dt>− {c.libelle}</dt><dd className="my-num">{euros(c.montantCentimes)}</dd></div>)}
          <dt style={{ fontWeight: 600 }}>Marge contributive</dt><dd className="my-prix" data-testid="synthese-marge-contributive">{euros(s.margeContributiveCentimes)} <span className="my-secondaire my-petit">({taux(s.tauxMargeContributiveBp)})</span></dd>
          <dt>− Pertes de stock valorisées</dt><dd className="my-num" data-testid="synthese-pertes-stock">{euros(s.ajustementsStock.pertesCentimes)}</dd>
          <dt>+ Gains de stock</dt><dd className="my-num">{euros(s.ajustementsStock.gainsCentimes)}</dd>
          <dt style={{ fontWeight: 600 }}>Marge contributive après stock</dt><dd className="my-prix" data-testid="synthese-mc-apres-ajustements">{euros(s.margeContributiveApresAjustementsCentimes)}</dd>
          <dt>Bénéfice net comptable</dt><dd className="my-secondaire" data-testid="synthese-benefice-net">Non calculé</dd>
        </dl>
      </Card>
      <Card as="section" aria-labelledby="t-couts" style={{ minWidth: 0 }}>
        <h2 id="t-couts" className="my-titre-4" style={{ marginBottom: 12 }}>Coûts logistiques et pertes</h2>
        <dl className="my-dl">
          <dt>Frais d’approche</dt><dd><span className="my-num">{euros(s.fraisApprocheInclusCentimes)}</span><span className="my-petit my-secondaire" style={{ display: 'block' }}>transport entrant, réception, stockage — déjà inclus dans le coût de revient</span></dd>
          <dt>Transport sortant</dt>
          <dd><span className="my-num" data-testid="synthese-transport">{euros(s.transportSortant.montantCentimes)}</span><span className="my-petit my-secondaire" style={{ display: 'block' }}>supporté par MYRON — {s.transportSortant.confirmeNbCommandes} tarif(s) confirmé(s) · {s.transportSortant.estimeNbCommandes > 0 ? <Badge ton="alerte">{s.transportSortant.estimeNbCommandes} estimé(s)</Badge> : '0 estimé'}</span></dd>
          <dt>Retours mis au rebut</dt><dd><span className="my-num">{euros(s.pertesCasseRetoursCentimes)}</span><span className="my-petit my-secondaire" style={{ display: 'block' }}>déjà inclus dans la marge brute (coût non repris)</span></dd>
          <dt>Ajustements de stock</dt>
          <dd className="my-petit">{s.ajustementsStock.nbMouvements} mouvement(s){s.ajustementsStock.nbNonValorises > 0 && <> · <Badge ton="alerte">{s.ajustementsStock.nbNonValorises} non valorisé(s)</Badge></>}</dd>
        </dl>
      </Card>
    </div>
  );
}
